import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Sparkles, ShieldCheck, ArrowRight } from "lucide-react";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { BillingToggle } from "@/components/billing/BillingToggle";
import { useBillingOptions, findOption } from "@/lib/billing";

const FADE = { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.5 } };

const TIERS = [
  { id: "free", name: "Free", price: "$0", period: "forever", blurb: "For hosts getting started.", features: ["10 assessments / month", "Deterministic risk score", "Basic reports", "Single property"], cta: "Start free", highlight: false },
  { id: "pro", name: "Pro Host", price: "$19", period: "/month", blurb: "A professional risk management platform.", features: ["Unlimited risk assessments", "Downloadable PDF evidence reports", "Evidence Centre + saved reports", "Risk analytics dashboard", "Risk trends & statistics", "AI risk summaries", "Full assessment history"], cta: "Start Pro Host", highlight: true },
  { id: "pm", name: "Property Manager", price: "$99", period: "/month", blurb: "For portfolios & teams.", features: ["Everything in Pro Host", "Multi-property management", "Portfolio analytics", "Team access & roles", "Advanced reporting"], cta: "Start Property Manager", highlight: false },
];

const COMPARE = [
  { label: "Monthly assessments", free: "10", pro: "Unlimited", pm: "Unlimited" },
  { label: "Risk score & sub-scores", free: true, pro: true, pm: true },
  { label: "AI risk summaries", free: false, pro: true, pm: true },
  { label: "Downloadable PDF evidence reports", free: false, pro: true, pm: true },
  { label: "Evidence Centre & saved reports", free: false, pro: true, pm: true },
  { label: "Risk analytics dashboard & trends", free: false, pro: true, pm: true },
  { label: "Assessment history", free: "Limited", pro: "Full", pm: "Full" },
  { label: "Multi-property management", free: false, pro: false, pm: true },
  { label: "Portfolio analytics", free: false, pro: false, pm: true },
  { label: "Team access & roles", free: false, pro: false, pm: true },
  { label: "Advanced reporting", free: false, pro: false, pm: true },
];

const Cell = ({ v }) => {
  if (v === true) return <Check className="mx-auto h-4 w-4 text-[hsl(var(--risk-low))]" />;
  if (v === false) return <X className="mx-auto h-4 w-4 text-muted-foreground/40" />;
  return <span className="text-sm">{v}</span>;
};

export default function PublicPricing() {
  const [interval, setInterval] = useState("monthly");
  const { options } = useBillingOptions();
  useEffect(() => {
    trackEvent("pricing_viewed", "/pricing");
  }, []);

  // Price display per tier honouring the selected billing interval.
  const priceFor = (tierId) => {
    if (tierId === "free") return { price: "$0", period: "forever" };
    const opt = findOption(options, tierId, interval);
    if (!opt) {
      // Fallback to the tier's default monthly display while options load.
      return null;
    }
    return {
      price: `$${Number.isInteger(opt.amount) ? opt.amount : opt.amount.toFixed(2)}`,
      period: opt.interval_label,
      perMonth: interval !== "monthly" ? opt.per_month_equivalent : null,
      available: opt.available,
    };
  };

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      <section className="hero-gradient">
        <div className="mx-auto max-w-6xl px-4 pb-10 pt-16 text-center sm:px-6">
          <motion.div {...FADE}>
            <span className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--risk-low))]" /> No card required to start
            </span>
            <h1 className="mt-5 font-display text-4xl font-semibold tracking-tight sm:text-5xl">Simple, transparent pricing</h1>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">Start free. Upgrade when you need AI summaries, unlimited checks and portfolio tools. Prices in AUD.</p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mb-8 flex flex-col items-center gap-2">
          <BillingToggle value={interval} onChange={setInterval} options={options} savingsPlan="pro" bestValueInterval="12mo" />
          {interval !== "monthly" && (
            <p className="text-xs text-muted-foreground">Pay upfront and save — billed {findOption(options, "pro", interval)?.interval_label || ""}, renews automatically.</p>
          )}
        </div>
        <div className="grid gap-5 lg:grid-cols-3">
          {TIERS.map((t) => {
            const pricing = priceFor(t.id) || { price: t.price, period: t.period };
            const intervalQ = t.id === "free" ? "" : `?plan=${t.id}&interval=${interval}`;
            return (
            <div key={t.id} className={`flex flex-col rounded-2xl border bg-card p-6 ${t.highlight ? "border-primary/30 shadow-[0_10px_30px_rgba(15,23,42,0.10)]" : ""}`} data-testid={`public-pricing-${t.id}`}>
              {t.highlight && <span className="mb-3 inline-flex w-fit items-center gap-1 rounded-full bg-primary px-2.5 py-0.5 text-xs font-medium text-primary-foreground"><Sparkles className="h-3 w-3" /> Most popular</span>}
              <h3 className="font-display text-lg font-semibold">{t.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t.blurb}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl font-semibold" data-testid={`public-price-${t.id}`}>{pricing.price}</span>
                <span className="text-sm text-muted-foreground">{pricing.period}</span>
              </div>
              {pricing.perMonth ? (
                <p className="mt-1 text-xs text-[hsl(var(--risk-low))]">≈ ${pricing.perMonth}/month equivalent</p>
              ) : <p className="mt-1 h-4" />}
              <ul className="mt-5 flex-1 space-y-2.5 text-sm">
                {t.features.map((f) => <li key={f} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--risk-low))]" /><span>{f}</span></li>)}
              </ul>
              <div className="mt-6">
                <Link to={`/signup${intervalQ}`}><Button className="w-full" variant={t.highlight ? "default" : "outline"} data-testid={`public-pricing-cta-${t.id}`}>{t.cta}</Button></Link>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* Comparison table */}
      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6">
        <motion.div {...FADE} className="overflow-hidden rounded-2xl border bg-card">
          <div className="hidden grid-cols-4 border-b bg-secondary/40 px-6 py-4 text-sm font-semibold sm:grid">
            <span>Compare plans</span>
            <span className="text-center">Free</span>
            <span className="text-center">Pro Host</span>
            <span className="text-center">Property Manager</span>
          </div>
          {COMPARE.map((row) => (
            <div key={row.label} className="grid grid-cols-2 items-center border-b px-6 py-3.5 text-sm last:border-0 sm:grid-cols-4">
              <span className="text-muted-foreground">{row.label}</span>
              <span className="hidden text-center sm:block"><Cell v={row.free} /></span>
              <span className="hidden text-center sm:block"><Cell v={row.pro} /></span>
              <span className="hidden text-center sm:block"><Cell v={row.pm} /></span>
              {/* mobile compact */}
              <div className="flex justify-end gap-4 sm:hidden">
                <span className="flex flex-col items-center text-xs text-muted-foreground">Free<Cell v={row.free} /></span>
                <span className="flex flex-col items-center text-xs text-muted-foreground">Pro<Cell v={row.pro} /></span>
                <span className="flex flex-col items-center text-xs text-muted-foreground">PM<Cell v={row.pm} /></span>
              </div>
            </div>
          ))}
        </motion.div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
        <div className="rounded-2xl bg-primary px-6 py-12 text-center text-primary-foreground sm:px-12">
          <h2 className="font-display text-3xl font-semibold tracking-tight">Protect your property today.</h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-foreground/80">Free to start, no card required. Upgrade anytime.</p>
          <Link to="/signup"><Button size="lg" variant="secondary" className="mt-6 gap-2">Get started free <ArrowRight className="h-4 w-4" /></Button></Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
